/**
 * Vuex module for MCP token self-service.
 *
 * Talks to 3 Cloud Functions in project people2-admin (us-central1):
 *   POST   mintMcpToken          — body { note? }
 *   GET    listMyMcpTokens       — ?include_revoked=true
 *   POST   revokeMyMcpToken      — body { hash }
 *
 * Every call sends the current user's Firebase Auth ID token
 * as `Authorization: Bearer ...`. The Cloud Functions verify the
 * token and derive role/scope from RTDB user_type so the client
 * cannot escalate privileges.
 */
import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'

const ENDPOINTS = {
  mint: 'https://us-central1-people2-admin.cloudfunctions.net/mintMcpToken',
  list: 'https://us-central1-people2-admin.cloudfunctions.net/listMyMcpTokens',
  revoke: 'https://us-central1-people2-admin.cloudfunctions.net/revokeMyMcpToken',
}

export type McpTokenRow = {
  hash: string
  hash_short: string
  email: string
  role: 'ic' | 'manager' | 'leadership' | 'admin'
  scope: 'self' | 'team' | 'org'
  created_at: string | null
  created_by: string | null
  source: string
  revoked: boolean
  revoked_at: string | null
  note: string | null
}

export type McpMintResult = {
  token: string // raw — show ONCE
  hash: string
  email: string
  name: string | null
  company_id: string
  team: string | null
  role: string
  scope: string
  created_at: string
  user_type: string | null
  config: Record<string, unknown>
}

type McpState = {
  tokens: McpTokenRow[]
  loading: boolean
  error: string | null
  lastMinted: McpMintResult | null
  includeRevoked: boolean
}

async function getIdToken(): Promise<string> {
  const user = firebase.auth().currentUser
  if (!user) {
    throw new Error('not_signed_in')
  }
  // forceRefresh = false — Firebase caches and auto-refreshes
  return user.getIdToken(false)
}

function extractErrorMessage(err: any): string {
  if (err && err.response && err.response.data) {
    if (err.response.data.message) return err.response.data.message
    if (err.response.data.error) return err.response.data.error
  }
  if (err && err.message) return err.message
  return 'Unknown error'
}

export default {
  namespaced: true,

  state: (): McpState => ({
    tokens: [],
    loading: false,
    error: null,
    lastMinted: null,
    includeRevoked: false,
  }),

  mutations: {
    SET_LOADING(state: McpState, v: boolean) {
      state.loading = v
    },
    SET_ERROR(state: McpState, v: string | null) {
      state.error = v
    },
    SET_TOKENS(state: McpState, v: McpTokenRow[]) {
      state.tokens = v
    },
    SET_LAST_MINTED(state: McpState, v: McpMintResult | null) {
      state.lastMinted = v
    },
    SET_INCLUDE_REVOKED(state: McpState, v: boolean) {
      state.includeRevoked = v
    },
  },

  actions: {
    async fetchMyTokens(
      { commit, state }: any,
      payload: { includeRevoked?: boolean } = {}
    ) {
      const includeRevoked =
        typeof payload.includeRevoked === 'boolean'
          ? payload.includeRevoked
          : state.includeRevoked
      commit('SET_INCLUDE_REVOKED', includeRevoked)
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const idToken = await getIdToken()
        const url = includeRevoked
          ? `${ENDPOINTS.list}?include_revoked=true`
          : ENDPOINTS.list
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${idToken}` },
          timeout: 15000,
        })
        commit('SET_TOKENS', (res.data && res.data.tokens) || [])
      } catch (err) {
        commit('SET_ERROR', extractErrorMessage(err))
        commit('SET_TOKENS', [])
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async mintToken(
      { commit, dispatch }: any,
      payload: { note?: string } = {}
    ): Promise<McpMintResult> {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const idToken = await getIdToken()
        const res = await axios.post(
          ENDPOINTS.mint,
          { note: payload.note != null ? payload.note : null },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
            timeout: 30000,
          }
        )
        const result = res.data as McpMintResult
        commit('SET_LAST_MINTED', result)
        // Re-fetch list so the new token shows up
        await dispatch('fetchMyTokens')
        return result
      } catch (err) {
        commit('SET_ERROR', extractErrorMessage(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async revokeToken({ commit, dispatch }: any, payload: { hash: string }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const idToken = await getIdToken()
        await axios.post(
          ENDPOINTS.revoke,
          { hash: payload.hash },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            },
            timeout: 15000,
          }
        )
        await dispatch('fetchMyTokens')
      } catch (err) {
        commit('SET_ERROR', extractErrorMessage(err))
        throw err
      } finally {
        commit('SET_LOADING', false)
      }
    },

    clearLastMinted({ commit }: any) {
      commit('SET_LAST_MINTED', null)
    },
  },

  getters: {
    activeTokens: (state: McpState) =>
      state.tokens.filter((t) => !t.revoked),
    revokedTokens: (state: McpState) =>
      state.tokens.filter((t) => t.revoked),
    hasLastMinted: (state: McpState) => state.lastMinted !== null,
  },
}
